import moment from 'moment';
import { PARCELS_CONSTANTS } from '../constants/parcelsConstants';
import { isPhytoTaskNotSeed } from './taskTypes';

export const buildTracksFromWorksites = (worksites) => {
  const worksitesWithTracks = (worksites || []).filter((w) => !!w.tracks);
  if (!worksitesWithTracks?.length) return null;
  const tracks = {
    type: 'FeatureCollection',
  };
  const features = [];
  worksitesWithTracks.forEach((worksite) => {
    const activeSession = {};
    activeSession.geometry = worksite.track;
    activeSession.properties = {
      device_session_id: worksite.device_session_id,
      fromDate: worksite.from_date,
      equipment_model_type: worksite?.equipment_model,
    };
    activeSession.type = 'Feature';
    features.push(activeSession);
  });
  tracks.features = features;
  return tracks;
};

export function getParcelDataFromWorksite(worksite) {
  const parcelsById = {};
  for (const equipmentSession of worksite.sessions || []) {
    for (const parcelSession of equipmentSession.parcel_sessions || []) {
      parcelsById[parcelSession.parcel_id] ??= {
        id: parcelSession.parcel_id,
        parcel_name: parcelSession.parcel_name,
        parcel_area: parcelSession.parcel_area,
        from_date: parcelSession.from_date,
        to_date: parcelSession.to_date,
        motor_duration: 0,
        distance: 0,
        area: 0,
        unit: 0,
      };

      parcelsById[parcelSession.parcel_id].from_date = moment(parcelSession.from_date).isBefore(
        moment(parcelsById[parcelSession.parcel_id].from_date),
      )
        ? parcelSession.from_date
        : parcelsById[parcelSession.parcel_id].from_date;
      parcelsById[parcelSession.parcel_id].to_date = moment(parcelSession.to_date).isAfter(
        moment(parcelsById[parcelSession.parcel_id].to_date),
      )
        ? parcelSession.to_date
        : parcelsById[parcelSession.parcel_id].to_date;
      parcelsById[parcelSession.parcel_id].motor_duration += parcelSession.motor_duration;
      parcelsById[parcelSession.parcel_id].distance += parcelSession.distance;
      parcelsById[parcelSession.parcel_id].area += parcelSession.area;
      parcelsById[parcelSession.parcel_id].unit += parcelSession.unit;
    }
  }
  return Object.values(parcelsById);
}

export function getEquipmentFromWorksite(worksite) {
  if (!worksite) return;
  return worksite.equipment_instances.find(
    (e) => (e.equipment_model || e.equipmentModel?.type) === worksite.equipment_model,
  );
}

/**
 * @param {import('../slices/worksites').Worksite} worksite
 *
 * @returns {import('../slices/worksites').WorksiteKey | 'error'}
 */
export function getWorksiteKey(worksite) {
  const equipment = getEquipmentFromWorksite(worksite);
  if (!equipment) return 'error';
  return `${equipment.id}-${worksite.cluster_id}-${worksite.device_session_id}`;
}

export function getWorksiteKeyFromTrack(feature) {
  if (!feature?.properties) return 'error';
  return `${feature.properties.equipment_instance_id}-${feature.properties.cluster_id}-${feature.properties.device_session_id}`;
}

/**
 * @param {Worksite} worksite
 *
 * @returns {number | undefined} `undefined` if this worksite has two or zero drivers, and the driver’s id if it’s the
 *   only one
 */
function getUniqueDriverIdFromWorksite(worksite) {
  const userIds = new Set(
    worksite.sessions
      .map((s) => s.parcel_sessions)
      .flat()
      .map((p) => p?.user_id)
      .filter(Boolean),
  );
  return userIds.size === 1 ? userIds.values().next().value : undefined;
}
export function getCompiledWorksitesData(worksites) {
  const result = {
    startDate: null,
    endDate: null,
    equipmentIds: [],
    driverIds: [],
    clusterIds: [],
  };

  for (const worksite of worksites) {
    if (result.startDate) {
      result.startDate = moment.min(result.startDate, moment(worksite.from_date));
    } else {
      result.startDate = moment(worksite.from_date);
    }

    if (result.endDate) {
      result.endDate = moment.max(result.endDate, moment(worksite.to_date));
    } else {
      result.endDate = moment(worksite.to_date);
    }

    const equipmentId = getEquipmentFromWorksite(worksite)?.id;
    if (equipmentId) {
      result.equipmentIds = [...new Set(result.equipmentIds.concat([equipmentId]))];
    }

    const driverId = getUniqueDriverIdFromWorksite(worksite);
    if (driverId) {
      result.driverIds = [...new Set(result.driverIds.concat(driverId))];
    }

    if (worksite.cluster_id) {
      result.clusterIds = [...new Set(result.clusterIds.concat(worksite.cluster_id))];
    }
  }

  return result;
}

export function getWorksiteParcelIdsSortedByDate(worksites, ownerClusterId, clientIds, allParcelsById) {
  const parcelsById = {};

  for (const worksite of worksites) {
    const parcels = getParcelDataFromWorksite(worksite).filter((p) =>
      filterWorksiteParcelsForTask(p.id, ownerClusterId, clientIds, allParcelsById),
    );
    for (const parcel of parcels) {
      if (!parcelsById[parcel.id]) {
        parcelsById[parcel.id] = parcel;
      } else if (moment(parcel.from_date).isBefore(moment(parcelsById[parcel.id].from_date))) {
        parcelsById[parcel.id].from_date = parcel.from_date;
      }
    }
  }

  return Object.entries(parcelsById)
    .sort(([, a], [, b]) => moment(a.from_date).diff(moment(b.from_date)))
    .map(([id]) => Number(id));
}

export function getParcelAreaFromWorksites(worksites, parcelId) {
  let area = 0;
  for (const worksite of worksites) {
    const parcels = getParcelDataFromWorksite(worksite);
    const parcel = parcels.find((p) => p.id === parcelId);
    if (parcel) {
      area += parcel.area || 0;
    }
  }
  return area;
}

export function filterWorksiteParcelsForTask(parcelId, ownerClusterId, clientIds = [], parcelsById = {}) {
  const parcel = parcelsById[parcelId];
  const excludeNonAnonFromOwner = !clientIds.includes(ownerClusterId);
  return (
    !parcel || !excludeNonAnonFromOwner || (parcel.current_tenant_cluster_id ?? parcel.cluster_id) !== ownerClusterId
  );
}

/**
 * Returns a `parcelId -> parcel_category` lookup. Reads the category from the worksites' `worked_parcel_with_tasks`
 * (always available on the worksite payload) and falls back to the parcels store. This matters because a worksite's
 * parcels are not necessarily in the store yet when it gets selected (they are fetched afterwards).
 */
function getParcelCategoryGetter(worksites, parcelsById = {}) {
  const categoryById = {};
  for (const worksite of worksites || []) {
    for (const p of worksite.worked_parcel_with_tasks || []) {
      categoryById[p.parcel_id] = p.parcel_category;
    }
  }
  return (parcelId) => categoryById[parcelId] ?? parcelsById[parcelId]?.category;
}

/**
 * Parcels of a worksite that should be auto-selected for a task: eligible parcels minus farmhouse parcels on a phyto
 * (non-seed) task. Farmhouse parcels stay visible in the list but must not be checked automatically.
 */
export function getWorksiteParcelsToSelectForTask(worksite, ownerClusterId, clientIds, parcelsById, taskType) {
  const isPhytoNotSeed = isPhytoTaskNotSeed(taskType);
  const getCategory = getParcelCategoryGetter([worksite], parcelsById);
  return getParcelDataFromWorksite(worksite).filter(
    (p) =>
      filterWorksiteParcelsForTask(p.id, ownerClusterId, clientIds, parcelsById) &&
      !(isPhytoNotSeed && getCategory(p.id) === PARCELS_CONSTANTS.CATEGORY.FARMHOUSE),
  );
}

/**
 * Removes farmhouse parcel IDs (coming from worksites) on a phyto (non-seed) task. Used when pre-selecting worksite
 * parcels into a work order.
 */
export function excludeFarmhouseWorksiteParcelIds(parcelIds, worksites, parcelsById, taskType) {
  if (!isPhytoTaskNotSeed(taskType)) return parcelIds;
  const getCategory = getParcelCategoryGetter(worksites, parcelsById);
  return parcelIds.filter((id) => getCategory(id) !== PARCELS_CONSTANTS.CATEGORY.FARMHOUSE);
}
